import { BarcodeFormat, BinaryBitmap, DecodeHintType, HTMLCanvasElementLuminanceSource, HybridBinarizer, MultiFormatReader } from "@zxing/library";
import { useCallback, useEffect, useRef } from "react";

/**
 * Grayscale + contrast stretch [min,max] → [0,255]. Helps low-contrast codes
 * (e.g. laser-etched Data Matrix on dark surfaces). Returns a new canvas.
 */
function enhanceContrast(source) {
    const { width, height } = source;
    const srcCtx = source.getContext("2d");
    if (!srcCtx) throw new Error("Canvas 2D context unavailable");

    const imageData = srcCtx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const gray = new Uint8ClampedArray(width * height);
    let lo = 255;
    let hi = 0;
    for (let i = 0; i < gray.length; i++) {
        const lum = Math.round(
            0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]
        );
        gray[i] = lum;
        if (lum < lo) lo = lum;
        if (lum > hi) hi = lum;
    }

    const range = hi - lo || 1;
    const out = document.createElement("canvas");
    out.width = width;
    out.height = height;
    const outCtx = out.getContext("2d");
    if (!outCtx) throw new Error("Canvas 2D context unavailable");
    const outData = outCtx.createImageData(width, height);
    for (let i = 0; i < gray.length; i++) {
        const v = Math.round(((gray[i] - lo) / range) * 255);
        outData.data[i * 4] = v;
        outData.data[i * 4 + 1] = v;
        outData.data[i * 4 + 2] = v;
        outData.data[i * 4 + 3] = 255;
    }
    outCtx.putImageData(outData, 0, 0);
    return out;
}

/**
 * Returns a new canvas with pixels inverted (255 − v). ZXing expects dark-on-light;
 * laser-etched codes on dark surfaces are light-on-dark, so inverting helps.
 */
function invertCanvas(source) {
    const { width, height } = source;
    const out = document.createElement("canvas");
    out.width = width;
    out.height = height;
    const outCtx = out.getContext("2d");
    if (!outCtx) throw new Error("Canvas 2D context unavailable");
    outCtx.drawImage(source, 0, 0);
    const imageData = outCtx.getImageData(0, 0, width, height);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
        d[i] = 255 - d[i];
        d[i + 1] = 255 - d[i + 1];
        d[i + 2] = 255 - d[i + 2];
    }
    outCtx.putImageData(imageData, 0, 0);
    return out;
}

export const useBarcode = (canvasRef) => {
    const hints = useRef(new Map());
    const reader = useRef(new MultiFormatReader());

    useEffect(() => {
        const formats = [
            BarcodeFormat.DATA_MATRIX,
            BarcodeFormat.CODE_128,
            BarcodeFormat.CODE_39,
            BarcodeFormat.EAN_13
        ];
        hints.current.set(DecodeHintType.POSSIBLE_FORMATS, formats);
        hints.current.set(DecodeHintType.CHARACTER_SET, "UTF-8");
        hints.current.set(DecodeHintType.TRY_HARDER, true);
        reader.current.setHints(hints.current);
    }, []);

    const scanForBarcode = useCallback((imageSrc) => {
        return new Promise((resolve) => {
            if (!imageSrc || !canvasRef.current) {
                resolve(undefined);
                return;
            }

            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");

            const img = new Image();
            img.onerror = () => resolve(undefined);
            img.onload = () => {
                const width = img.width;
                const height = img.height;

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                function tryDecode(canvasToDecode) {
                    try {
                        const luminanceSource = new HTMLCanvasElementLuminanceSource(canvasToDecode);
                        const binaryBitmap = new BinaryBitmap(new HybridBinarizer(luminanceSource));
                        const result = reader.current.decode(binaryBitmap);
                        return result.getText();
                    } catch (e) {
                        if (process.env.NODE_ENV === "development" && e instanceof Error && e.name !== "NotFoundException") {
                            console.error("An error occurred during decoding:", e); // eslint-disable-line no-console
                        }
                        return null;
                    }
                }

                // Attempt 1: raw frame
                let text = tryDecode(canvas);
                if (text) {
                    resolve(text);
                    return;
                }

                // Attempt 2: contrast-stretched grayscale
                const enhanced = enhanceContrast(canvas);
                text = tryDecode(enhanced);
                if (text) {
                    resolve(text);
                    return;
                }

                // Attempt 3: enhanced + inverted (for light-on-dark codes)
                text = tryDecode(invertCanvas(enhanced));
                resolve(text ?? undefined);
            };
            img.src = imageSrc;
        });
    }, [canvasRef]);

    return scanForBarcode;
};
